// @ts-nocheck
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  createCompany,
  fetchCompaniesForLawFirm,
  type CompanyRecord,
  type CreateCompanyInput,
} from '../../services/FirmCompaniesApiClient';
import { useAuth } from '../../contexts/AuthContext';
import { getLawFirmIdFromJwt } from '../../services/LoginApiClient';
import { getParachuteAuthToken } from '../../utils/parachuteAuthToken';
import { DEFAULT_LOCAL_LAW_FIRM_GUID } from '../../constants/firmWorkspace';
import { useFirmLawFirmOptional } from './FirmLawFirmContext';

/** List row: a company under the current law firm; 280G analyses are tracked per company. */
export interface CompanySummary {
  id: string;
  companyName: string;
  closingDate: string;
  analysesCount: number;
  overThresholdCount: number;
  lastUpdated: string;
  dealStructure: string;
  dealRole: string;
  listingStatus: string;
  /** Overall deal / 280G workflow (intake, in_progress, review, complete, on_hold). */
  workStatus: string;
  notes: string;
}

function toSummary(row: CompanyRecord): CompanySummary {
  return {
    id: row.id,
    companyName: row.companyName,
    closingDate: row.closingDate,
    analysesCount: row.analysesCount,
    overThresholdCount: row.overThresholdCount,
    lastUpdated: row.lastUpdated,
    dealStructure: row.dealStructure ?? 'unsure',
    dealRole: row.dealRole ?? 'unsure',
    listingStatus: row.listingStatus ?? 'unsure',
    workStatus: row.workStatus?.trim() || 'intake',
    notes: row.notes ?? '',
  };
}

type FirmCompaniesContextValue = {
  companies: CompanySummary[];
  loading: boolean;
  error: string | null;
  /** Law firm tenant id (`X-Law-Firm-Id` on the companies API). */
  lawFirmId: string;
  refreshCompanies: () => Promise<void>;
  addCompany: (input: CreateCompanyInput) => Promise<CompanySummary>;
};

const FirmCompaniesContext = createContext<FirmCompaniesContextValue | null>(null);

function formatCompaniesFetchError(err: unknown): string {
  const ax = err as {
    message?: string;
    code?: string;
    response?: { status?: number };
  };
  const status = ax.response?.status;
  if (status === 504 || status === 502) {
    return 'Companies API is not responding. Ensure the backend at REACT_APP_API_URL (see `src/config.ts`) is running.';
  }
  if (ax.code === 'ERR_NETWORK' || ax.message === 'Network Error') {
    return 'Cannot reach the companies API. Check REACT_APP_API_URL and that the API is running.';
  }
  if (typeof status === 'number') {
    return `Request failed with status code ${status}`;
  }
  if (typeof ax.message === 'string' && ax.message) {
    return ax.message;
  }
  return 'Failed to load companies';
}

export function FirmCompaniesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const law = useFirmLawFirmOptional();
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Must match JWT `law_firm_id` — API returns 403 if the path guid disagrees.
   * Prefer workspace `lawFirm.id`; before that loads, use the same id from the bearer token.
   * `DEFAULT_LOCAL_LAW_FIRM_GUID` is only for local mock when no JWT firm id exists.
   */
  const lawFirmId = useMemo(() => {
    const fromApi = law?.lawFirm?.id?.trim();
    if (fromApi) return fromApi;
    const fromJwt = getLawFirmIdFromJwt(getParachuteAuthToken());
    if (fromJwt) return fromJwt;
    return DEFAULT_LOCAL_LAW_FIRM_GUID;
  }, [law?.lawFirm?.id, user?.token]);

  const refreshCompanies = useCallback(async () => {
    setError(null);
    try {
      const rows = await fetchCompaniesForLawFirm(lawFirmId);
      const list = rows.map(toSummary).sort((a, b) => a.companyName.localeCompare(b.companyName));
      setCompanies(list);
    } catch (e) {
      setError(formatCompaniesFetchError(e));
      setCompanies([]);
    }
  }, [lawFirmId]);

  useEffect(() => {
    if (law?.loading) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    refreshCompanies().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [law?.loading, refreshCompanies]);

  const addCompany = useCallback(
    async (input: CreateCompanyInput) => {
      const row = await createCompany(lawFirmId, input);
      const summary = toSummary(row);
      setCompanies((prev) =>
        [...prev.filter((c) => c.id !== summary.id), summary].sort((a, b) => a.companyName.localeCompare(b.companyName))
      );
      setError(null);
      return summary;
    },
    [lawFirmId]
  );

  const value = useMemo(
    () => ({ companies, loading, error, lawFirmId, refreshCompanies, addCompany }),
    [companies, loading, error, lawFirmId, refreshCompanies, addCompany]
  );

  return <FirmCompaniesContext.Provider value={value}>{children}</FirmCompaniesContext.Provider>;
}

export function useFirmCompanies(): FirmCompaniesContextValue {
  const ctx = useContext(FirmCompaniesContext);
  if (!ctx) {
    throw new Error('useFirmCompanies must be used within FirmCompaniesProvider');
  }
  return ctx;
}
